import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Logo } from "../components/Logo";
import { api, errMsg, safeRedirect, label } from "../lib/api";
import { useAuth } from "../lib/auth";
import { t, LANGS, getLang, setLang } from "../lib/i18n";
import { toast } from "sonner";

const Shell = ({ children, wide }) => {
  const [, re] = useState(0);
  return (
    <div className="tro-grid-bg flex min-h-screen items-center justify-center p-4">
      <div className={`tro-card w-full ${wide ? "max-w-xl" : "max-w-md"} p-7 shadow-sm tro-fade md:p-9`}>
        <div className="mb-7 flex items-center justify-between">
          <Logo size={42} />
          <select data-testid="language-select" value={getLang()} onChange={(e) => { setLang(e.target.value); re((x) => x + 1); }} className="h-8 rounded-md border border-[#E6E9ED] bg-white px-2 text-xs font-semibold">
            {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {children}
      </div>
    </div>
  );
};

const Field = ({ label: l, ...p }) => (
  <label className="block"><span className="tro-label">{l}</span><Input className="mt-1.5 h-11" {...p} /></label>
);

export function GoogleButton({ redirect }) {
  const go = () => {
    if (safeRedirect(redirect)) sessionStorage.setItem("tro_redirect", redirect);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-button" className="flex h-12 w-full items-center justify-center gap-3 rounded-md border border-[#C5CBD1] bg-white text-sm font-bold text-[#17191C] transition-colors hover:bg-[#F7F8F9]">
      <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      Continue with Google
    </button>
  );
}

const OrLine = () => <div className="my-4 flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-[#6C757D]"><span className="h-px flex-1 bg-[#E6E9ED]" />or<span className="h-px flex-1 bg-[#E6E9ED]" /></div>;

export function AuthCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const { signIn } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    const target = safeRedirect(sessionStorage.getItem("tro_redirect")) || "/dashboard";
    sessionStorage.removeItem("tro_redirect");
    api.post("/auth/google/session", { session_id: sid }).then(async ({ data }) => {
      if (data.mfa_required) return nav(`/login?redirect=${encodeURIComponent(target)}`, { replace: true, state: { mfa_token: data.mfa_token } });
      await signIn(data.token);
      nav(target, { replace: true });
    }).catch((e) => {
      toast.error(errMsg(e));
      nav("/login", { replace: true });
    });
  }, []); // eslint-disable-line
  return null;
}

const DEMO = [["Referee", "referee@tro.demo"], ["Coach", "coach@tro.demo"], ["Club", "club@tro.demo"], ["League", "league@tro.demo"], ["4th Official", "fourth@tro.demo"], ["Commissioner", "commissioner@tro.demo"], ["Assessor", "assessor@tro.demo"], ["Player", "player@tro.demo"], ["Federation", "federation@tro.demo"], ["Fan", "fan@tro.demo"]];

export function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useAuth();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [mfa, setMfa] = useState(loc.state?.mfa_token || null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const done = async (token) => {
    await signIn(token);
    nav(safeRedirect(sp.get("redirect")) || "/dashboard", { replace: true });
  };
  const submit = async (e, creds = f) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", creds);
      if (data.mfa_required) setMfa(data.mfa_token);
      else await done(data.token);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa, code });
      await done(data.token);
    } catch (x) {
      setErr(errMsg(x));
    }
  };
  return (
    <Shell>
      <h1 className="text-3xl font-extrabold tracking-tight">{t("welcome")}</h1>
      <p className="mb-6 mt-1 text-sm text-[#6C757D]">Digital football management & refereeing system</p>
      {!mfa && <><GoogleButton redirect={sp.get("redirect")} /><OrLine /></>}
      {!mfa ? (
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <Field label="Email" type="email" data-testid="login-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          <Field label="Password" type="password" data-testid="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
          {err && <div className="rounded-md bg-[#F02030]/10 px-3 py-2 text-sm font-semibold text-[#F02030]" data-testid="login-error">{err}</div>}
          <Button data-testid="login-submit-button" disabled={busy} className="h-12 w-full text-base font-bold">{t("signin")}</Button>
          <div className="flex justify-between text-sm font-semibold">
            <Link to="/forgot-password" className="text-[#343A40] hover:underline" data-testid="forgot-password-link">{t("forgot")}</Link>
            <Link to={`/register${sp.get("redirect") ? `?redirect=${encodeURIComponent(sp.get("redirect"))}` : ""}`} className="text-[#17191C] hover:underline" data-testid="create-account-link">{t("create")}</Link>
          </div>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4" data-testid="mfa-form">
          <p className="text-sm">Enter the 6-digit code from your authenticator app.</p>
          <Field label="Authentication code" data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} />
          {err && <div className="text-sm font-semibold text-[#F02030]">{err}</div>}
          <Button data-testid="mfa-submit-button" className="h-12 w-full font-bold">Verify</Button>
        </form>
      )}
      <div className="mt-6 border-t border-[#E6E9ED] pt-4">
        <div className="tro-label mb-2">Demonstration accounts · password Demo@2026</div>
        <div className="flex flex-wrap gap-1.5">
          {DEMO.map(([l, e]) => (
            <button key={e} data-testid={`demo-login-${l.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => submit(null, { email: e, password: "Demo@2026" })}
              className="rounded-full border border-[#C5CBD1] bg-white px-3 py-1 text-xs font-bold text-[#343A40] transition-colors hover:bg-[#17191C] hover:text-white">{l}</button>
          ))}
        </div>
      </div>
    </Shell>
  );
}

const ROLES = ["fan", "referee", "assistant_referee", "fourth_official", "commissioner", "assessor", "coach", "player", "club_admin", "league_admin", "federation_admin", "var_operator"];

export function Register() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [orgs, setOrgs] = useState({ federations: [], leagues: [], clubs: [] });
  const [f, setF] = useState({ name: "", email: "", password: "", role: "fan", org_id: "" });
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/public/organisations").then((r) => setOrgs(r.data)).catch(() => {});
  }, []);
  const orgList = ["coach", "player", "club_admin"].includes(f.role) ? orgs.clubs : f.role === "federation_admin" ? orgs.federations : f.role === "fan" ? [] : f.role === "league_admin" || f.role === "var_operator" ? orgs.leagues : orgs.federations;
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/register", { ...f, org_id: f.org_id || null });
      await signIn(data.token);
      if (f.role !== "fan") toast.info("Your role request is pending organisation approval.");
      nav(safeRedirect(sp.get("redirect")) || "/dashboard", { replace: true });
    } catch (x) {
      setErr(errMsg(x));
    }
  };
  return (
    <Shell wide>
      <h1 className="text-3xl font-extrabold tracking-tight">{t("create")}</h1>
      <p className="mb-6 mt-1 text-sm text-[#6C757D]">One account for all your roles. Regulated roles require organisation approval.</p>
      <GoogleButton redirect={sp.get("redirect")} />
      <p className="mt-2 text-xs text-[#6C757D]">Google accounts start with Fan access; request official roles from your profile.</p>
      <OrLine />
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="register-form">
        <Field label="Full name" data-testid="register-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
        <Field label="Email" type="email" data-testid="register-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
        <Field label="Password (8+ characters)" type="password" data-testid="register-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} />
        <label className="block"><span className="tro-label">Register as</span>
          <select data-testid="register-role-select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value, org_id: "" })} className="mt-1.5 h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
            {ROLES.map((r) => <option key={r} value={r}>{label(r)}</option>)}
          </select></label>
        {orgList.length > 0 && (
          <label className="block sm:col-span-2"><span className="tro-label">Organisation</span>
            <select data-testid="register-org-select" value={f.org_id} onChange={(e) => setF({ ...f, org_id: e.target.value })} className="mt-1.5 h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
              <option value="">Select organisation (optional)</option>
              {orgList.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select></label>
        )}
        {err && <div className="text-sm font-semibold text-[#F02030] sm:col-span-2" data-testid="register-error">{err}</div>}
        <Button data-testid="register-submit-button" className="h-12 font-bold sm:col-span-2">{t("create")}</Button>
      </form>
      <p className="mt-4 text-center text-sm">Already registered? <Link to="/login" className="font-bold hover:underline" data-testid="back-to-login-link">{t("signin")}</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [v, setV] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = token ? await api.post("/auth/reset-password", { token, password: v }) : await api.post("/auth/forgot-password", { email: v });
      setMsg(token ? "Password updated. You can sign in now." : data.message);
    } catch (x) {
      setMsg(errMsg(x));
    }
  };
  return (
    <Shell>
      <h1 className="mb-4 text-2xl font-extrabold">{token ? "Set a new password" : t("forgot")}</h1>
      <form onSubmit={submit} className="space-y-4">
        <Field label={token ? "New password" : "Email"} type={token ? "password" : "email"} data-testid="forgot-input" value={v} onChange={(e) => setV(e.target.value)} required />
        <Button data-testid="forgot-submit-button" className="h-11 w-full font-bold">{token ? "Update password" : "Send reset link"}</Button>
        {msg && <div className="text-sm text-[#343A40]" data-testid="forgot-message">{msg}</div>}
      </form>
      <Link to="/login" className="mt-4 block text-sm font-bold hover:underline">Back to sign in</Link>
    </Shell>
  );
}

export function AcceptInvite() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", password: "" });
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/accept-invitation", { token: sp.get("token"), ...f });
      setMsg(`Invitation accepted for ${data.email}. Sign in to continue.`);
    } catch (x) {
      setMsg(errMsg(x));
    }
  };
  return (
    <Shell>
      <h1 className="mb-2 text-2xl font-extrabold">Accept invitation</h1>
      <p className="mb-4 text-sm text-[#6C757D]">Existing TRO users: leave the fields blank — the role is added to your account.</p>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-input" />
        <Field label="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="invite-password-input" />
        <Button className="h-11 w-full font-bold" data-testid="invite-submit-button">Accept</Button>
        {msg && <div className="text-sm" data-testid="invite-message">{msg}</div>}
      </form>
      <Link to="/login" className="mt-4 block text-sm font-bold hover:underline">Sign in</Link>
    </Shell>
  );
}
